<template>
  <div class="profile-picture-upload">
    <div class="current-avatar-display">
      <img v-if="hasUploadedAvatar && avatarUrl" :src="avatarUrl" alt="Current avatar" class="user-avatar user-avatar-medium" />
      <div v-else class="avatar-placeholder">
        <Camera :size="96" :stroke-width="1" />
      </div>
    </div>

    <!-- Upload Zone -->
    <div
      class="upload-zone"
      :class="{ 'drag-over': isDragging }"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="handleFileDrop"
      @click="triggerFileInput"
    >
      <input
        ref="fileInput"
        type="file"
        accept="image/jpeg,image/png,image/jpg"
        @change="handleFileSelect"
        style="display: none"
      />
      <p>{{ uploadText }}</p>
      <small>{{ uploadHint }}</small>
    </div>

    <!-- Preview with Crop (when image selected) -->
    <div v-if="selectedFile" class="preview-container">
      <div class="crop-container">
        <img ref="cropImage" :src="previewUrl" alt="Crop preview" />
      </div>
      <div class="crop-controls">
        <button @click="uploadAvatar" class="button" :disabled="uploading">
          {{ uploading ? uploadingText : uploadButtonText }}
        </button>
        <button @click="cancelUpload" class="button-outline" :disabled="uploading">
          {{ cancelText }}
        </button>
      </div>
    </div>

    <!-- Delete Button (if uploaded avatar exists) -->
    <div v-if="hasUploadedAvatar" class="delete-section">
      <button @click="deleteAvatar" class="button-delete" :disabled="deleting">
        {{ deleting ? deletingText : deleteButtonText }}
      </button>
    </div>

    <!-- Error/Success Messages -->
    <div v-if="message" class="alert" :class="messageType === 'error' ? 'alert-danger' : 'alert-success'">
      {{ message }}
    </div>
  </div>
</template>

<script>
import { Camera } from 'lucide-vue-next';
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';

export default {
  name: 'AvatarUploader',
  components: { Camera },
  props: {
    initialAvatarUrl: {
      type: String,
      default: ''
    },
    avatarType: {
      type: String,
      default: 'default'
    },
    defaultAvatarSvg: {
      type: String,
      default: ''
    },
    uploadUrl: {
      type: String,
      required: true
    },
    deleteUrl: {
      type: String,
      required: true
    },
    csrfToken: {
      type: String,
      required: true
    },
    uploadText: {
      type: String,
      default: 'Click or drag image here to upload avatar'
    },
    uploadHint: {
      type: String,
      default: 'JPG, PNG (max 5MB, recommended 512x512px)'
    },
    uploadButtonText: {
      type: String,
      default: 'Upload Avatar'
    },
    uploadingText: {
      type: String,
      default: 'Uploading...'
    },
    deleteButtonText: {
      type: String,
      default: 'Delete Avatar'
    },
    deletingText: {
      type: String,
      default: 'Deleting...'
    },
    cancelText: {
      type: String,
      default: 'Cancel'
    }
  },
  data() {
    return {
      avatarUrl: this.initialAvatarUrl,
      isDragging: false,
      selectedFile: null,
      previewUrl: null,
      cropper: null,
      uploading: false,
      deleting: false,
      message: '',
      messageType: 'success'
    };
  },
  computed: {
    hasUploadedAvatar() {
      return this.avatarType === 'uploaded' && this.avatarUrl;
    }
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.processFile(file);
      }
    },
    handleFileDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) {
        this.processFile(file);
      }
    },
    processFile(file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        this.showMessage('File is too large. Maximum size is 5MB.', 'error');
        return;
      }

      // Validate file type
      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        this.showMessage('Invalid file type. Please upload JPG or PNG.', 'error');
        return;
      }

      this.selectedFile = file;
      this.previewUrl = URL.createObjectURL(file);

      // Initialize cropper after image loads
      this.$nextTick(() => {
        this.initCropper();
      });
    },
    initCropper() {
      if (this.cropper) {
        this.cropper.destroy();
      }

      const image = this.$refs.cropImage;
      if (!image) return;

      this.cropper = new Cropper(image, {
        aspectRatio: 1, // Square for avatars
        viewMode: 1,
        dragMode: 'move',
        autoCropArea: 1,
        restore: false,
        guides: true,
        center: true,
        highlight: false,
        cropBoxMovable: true,
        cropBoxResizable: true,
        toggleDragModeOnDblclick: false
      });
    },
    async uploadAvatar() {
      if (!this.cropper) return;

      this.uploading = true;
      this.message = '';

      try {
        // Get cropped canvas
        const canvas = this.cropper.getCroppedCanvas({
          width: 512,
          height: 512,
          imageSmoothingQuality: 'high'
        });

        // Convert to blob
        const blob = await new Promise(resolve => {
          canvas.toBlob(resolve, 'image/jpeg', 0.9);
        });

        // Create form data
        const formData = new FormData();
        formData.append('avatar_image', blob, 'avatar.jpg');

        // Upload
        const response = await fetch(this.uploadUrl, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': this.csrfToken
          },
          body: formData
        });

        const data = await response.json();

        if (data.success) {
          this.avatarUrl = data.avatar_url;
          this.showMessage(data.message, 'success');
          this.cancelUpload();
          this.$emit('avatar-updated', data.avatar_url);
          // Reload page to update all avatar instances
          setTimeout(() => window.location.reload(), 1000);
        } else {
          this.showMessage(data.error || 'Upload failed', 'error');
        }
      } catch (error) {
        console.error('Upload error:', error);
        this.showMessage('Upload failed. Please try again.', 'error');
      } finally {
        this.uploading = false;
      }
    },
    cancelUpload() {
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
      }
      this.selectedFile = null;
      this.previewUrl = null;
      this.$refs.fileInput.value = '';
    },
    async deleteAvatar() {
      if (!confirm('Are you sure you want to delete your avatar?')) {
        return;
      }

      this.deleting = true;
      this.message = '';

      try {
        const response = await fetch(this.deleteUrl, {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': this.csrfToken,
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();

        if (data.success) {
          this.avatarUrl = data.avatar_url;
          this.showMessage(data.message, 'success');
          this.$emit('avatar-deleted');
          // Reload page to update all avatar instances
          setTimeout(() => window.location.reload(), 1000);
        } else {
          this.showMessage(data.error || 'Deletion failed', 'error');
        }
      } catch (error) {
        console.error('Delete error:', error);
        this.showMessage('Deletion failed. Please try again.', 'error');
      } finally {
        this.deleting = false;
      }
    },
    showMessage(text, type) {
      this.message = text;
      this.messageType = type;
      setTimeout(() => {
        this.message = '';
      }, 5000);
    }
  },
  beforeUnmount() {
    if (this.cropper) {
      this.cropper.destroy();
    }
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  }
};
</script>
